/**
 * SessionsViewer — /sessions (EGDL design "SessionsList").
 *
 *   Provider, all players   GET /api/sessions               (all verified-linked players' sessions)
 *   Provider, one player    GET /api/sessions?user_id=<id>
 *   Player                  GET /api/sessions?user_id=<own id>
 * 200 → array, 204 → empty body (never res.json() a 204).
 *
 * Search, pagination, status, live indicator (session:list from the relay), rows open
 * /sessions/:id; below 768 px rows become cards.
 *
 * Sorting (sessionSort.js): click a column header (again to turn it around), or on a phone
 * the Sort menu. The order is kept in the address, so Back from a session keeps it.
 *
 * Clinician-run sessions (sessionAccess.js): a provider's list includes their own guest
 * sessions, shown as "Guest" (Player filter "Guest sessions" = their own user_id); a session
 * a clinician started for a patient says "Started by …" under its game.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useLinkedPlayers } from './api/useLinkedPlayers';
import { useLiveList } from './SocketContext';
import {
    Button, DataTable, DotLabel, EmptyState, ErrorState, Icon, SearchField, SelectField, Skel, StatusBadge, cx, useIsMobile,
} from './components/ui';
import { fmtDuration, toMs } from './seriesExtractors';
import './SessionsViewer.css';
import { gameLabel } from './games';
import { GameCell, playerLabel, startedByLabel } from './sessionAccess';
import { DEFAULT_SORT, SORT_KEYS, nextSort, readSort, sortOptions, sortRows, writeSort } from './sessionSort';

const PER_PAGE = 10;

async function fetchSessions(authFetch, url) {
    const res = await authFetch(url);
    if (res.status === 204) return [];
    if (!res.ok) throw new Error(`The server returned an error (${res.status}).`);
    const text = await res.text();
    if (!text.trim()) return [];
    const data = JSON.parse(text);
    return Array.isArray(data) ? data : [];
}

function fmtStart(ms) {
    if (ms == null) return '—';
    const d = new Date(ms);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    const date = d.toLocaleDateString([], sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
    return `${date}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function lengthMs(s) {
    const a = toMs(s.startTime);
    const b = toMs(s.endTime);
    if (a != null && b != null && b > a) return b - a;
    const l = toMs(s.results?.length);
    return l && l > 0 ? l : null;
}

/** Page buttons: 1 … (cur-1) cur (cur+1) … last */
function pageList(count, cur) {
    if (count <= 7) return Array.from({ length: count }, (_, i) => i);
    const set = new Set([0, count - 1, cur - 1, cur, cur + 1].filter((i) => i >= 0 && i < count));
    const out = [];
    [...set].sort((a, b) => a - b).forEach((i, k, arr) => {
        if (k && i - arr[k - 1] > 1) out.push(`gap${i}`);
        out.push(i);
    });
    return out;
}

function LiveLink({ id }) {
    const stop = (e) => e.stopPropagation();
    return (
        <Link to={`/live/${id}`} className="sl-live" onClick={stop} onKeyDown={stop} aria-label="Live now — open the live view">
            <DotLabel color="var(--egdl-danger)" ink="var(--egdl-danger)" pulse>Live</DotLabel>
        </Link>
    );
}

export default function SessionsViewer() {
    const { authFetch, user } = useAuth();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const isProvider = user?.role === 'provider';
    const { players, nameOf } = useLinkedPlayers();
    const live = useLiveList();

    const [who, setWho] = useState('all');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const [state, setState] = useState({ list: [], loading: true, error: null });
    const [params, setParams] = useSearchParams();

    const url = useMemo(() => {
        if (!user?.user_id) return null;
        if (!isProvider) return `${API_BASE}/api/sessions?user_id=${encodeURIComponent(user.user_id)}`;
        return who === 'all' ? `${API_BASE}/api/sessions` : `${API_BASE}/api/sessions?user_id=${encodeURIComponent(who)}`;
    }, [user, isProvider, who]);

    const load = useCallback(async () => {
        if (!url) return;
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const list = await fetchSessions(authFetch, url);
            setState({ list, loading: false, error: null });
        } catch (e) {
            setState({ list: [], loading: false, error: e.message || 'The server returned an error.' });
        }
    }, [authFetch, url]);
    useEffect(() => { load(); }, [load]);

    // The old page cached the list in localStorage forever; drop that stale copy.
    useEffect(() => {
        if (!user?.user_id) return;
        try { localStorage.removeItem(`userData_${user.user_id}`); } catch { /* storage unavailable */ }
    }, [user]);

    const liveIds = useMemo(() => new Set((live.sessions || []).map((s) => s.sessionId)), [live]);
    const playerOpts = useMemo(() => {
        const seen = new Set();
        const out = [{ value: 'all', label: 'All linked players' }];
        players.forEach((p) => {
            if (!p.user_id || seen.has(p.user_id)) return;
            seen.add(p.user_id);
            out.push({ value: p.user_id, label: p.name });
        });
        // A clinician's own sessions are the guest sessions they ran.
        if (isProvider && user?.user_id) out.push({ value: user.user_id, label: 'Guest sessions' });
        return out;
    }, [players, isProvider, user]);

    const showWho = isProvider && (who === 'all' || who === user?.user_id);
    // the Player column is there only for a clinician's mixed list; without it, the default order
    const sort = readSort(params, showWho ? SORT_KEYS : SORT_KEYS.filter((k) => k !== 'who'));
    const { key: sortKey, dir: sortDir } = sort;
    const setSort = (next) => {
        setParams(writeSort(params, next), { replace: true });
        setPage(0);
    };
    const rows = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return sortRows(state.list
            .map((s) => {
                const start = toMs(s.startTime);
                const durMs = lengthMs(s);
                const whoText = playerLabel(s, nameOf);
                // who started it, and whether it was played offline and uploaded later
                const origin = [startedByLabel(s, user, nameOf), s.offline === true ? 'Played offline' : null].filter(Boolean).join(' · ') || null;
                return {
                    id: s._id,
                    who: whoText,
                    game: s.game ? gameLabel(s.game) : 'Untitled game',
                    origin,
                    ver: s.version || '—',
                    startMs: start,
                    start: fmtStart(start),
                    durMs,
                    dur: fmtDuration(durMs),
                    status: s.consolidated || null,
                    live: liveIds.has(s._id),
                    hay: [s.game, gameLabel(s.game, ''), s.version, s.location, whoText, origin, start != null ? new Date(start).toLocaleDateString() : ''].join(' ').toLowerCase(),
                };
            })
            .filter((r) => !needle || r.hay.includes(needle)), { key: sortKey, dir: sortDir });
    }, [state.list, q, nameOf, liveIds, user, sortKey, sortDir]);

    const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
    const cur = Math.min(page, pages - 1);
    const slice = rows.slice(cur * PER_PAGE, cur * PER_PAGE + PER_PAGE);
    const open = (id) => () => navigate(`/sessions/${id}`);

    const statusCell = (r) => (r.live ? <LiveLink id={r.id} /> : r.status ? <StatusBadge status={r.status} /> : <span className="eg-table__text eg-table__text--muted">—</span>);
    const columns = [
        ...(showWho ? [{ key: 'who', label: 'Player', w: '1.2fr', strong: true, sortable: true }] : []),
        {
            key: 'game',
            label: 'Game',
            sortable: true,
            w: showWho ? '1.2fr' : '1.6fr',
            render: (r) => (
                <span className={cx('eg-table__text', !showWho && 'eg-table__text--strong')} title={r.game}>
                    <GameCell game={r.game} origin={r.origin} />
                </span>
            ),
        },
        // on a narrow desktop: room for a label and its sort arrow, and for the widest status ("Incomplete")
        { key: 'ver', label: 'Version', w: 'minmax(100px, .6fr)', muted: true, sortable: true },
        { key: 'start', label: 'Started', w: '1.1fr', sortable: true },
        { key: 'dur', label: 'Duration', w: 'minmax(116px, .75fr)', align: 'right', sortable: true },
        { key: 'status', label: 'Status', w: 'minmax(132px, 1fr)', render: statusCell, sortable: true },
    ];

    const whoName = playerOpts.find((o) => o.value === who)?.label;
    let body;
    if (state.loading) {
        body = (
            <div className="sl-skel" aria-busy="true" aria-label="Loading sessions">
                <Skel h={44} raised />
                {[0, 1, 2, 3].map((i) => <Skel key={i} h={56} raised />)}
            </div>
        );
    } else if (state.error) {
        body = <ErrorState kicker="" title="Sessions didn’t load" onRetry={load}>{state.error}</ErrorState>;
    } else if (!state.list.length) {
        body = (
            <EmptyState title={isProvider && who === user?.user_id ? 'No guest sessions yet' : isProvider && who !== 'all' ? `No sessions for ${whoName} yet` : 'No sessions yet'} />
        );
    } else {
        body = (
            <>
                {!slice.length ? (
                    <div className="sl-nomatch">No sessions match “{q.trim()}”.</div>
                ) : isMobile ? (
                    <div role="list" aria-label="Sessions" className="sl-cards">
                        {slice.map((r) => (
                            <div
                                key={r.id}
                                role="listitem"
                                tabIndex={0}
                                className="sl-card"
                                onClick={open(r.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter') open(r.id)(); }}
                            >
                                <div className="sl-card__top">
                                    <span className="sl-card__title">{showWho ? `${r.who} · ${r.game}` : r.game}</span>
                                    {statusCell(r)}
                                </div>
                                <span className="eg-small eg-secondary">{[r.start, r.dur, `v${r.ver}`, r.origin].filter(Boolean).join(' · ')}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <DataTable
                        caption="Sessions"
                        columns={columns}
                        rows={slice.map((r) => ({ ...r, onClick: open(r.id) }))}
                        getKey={(r) => r.id}
                        forceMobile={false}
                        sort={sort}
                        onSort={(key) => setSort(nextSort(sort, key))}
                    />
                )}
                <nav aria-label="Pagination" className="sl-pager">
                    <span className="sl-pager__range">
                        {rows.length ? `${cur * PER_PAGE + 1}–${Math.min(rows.length, cur * PER_PAGE + PER_PAGE)} of ${rows.length} sessions` : '0 sessions'}
                    </span>
                    <Button variant="icon" aria-label="Previous page" disabled={cur === 0} onClick={() => setPage(cur - 1)}>
                        <Icon name="chevronLeft" size={20} />
                    </Button>
                    {pageList(pages, cur).map((p) => (typeof p === 'string' ? (
                        <span key={p} className="sl-pager__gap" aria-hidden="true">…</span>
                    ) : (
                        <button
                            key={p}
                            type="button"
                            className={cx('sl-pagebtn', p === cur && 'sl-pagebtn--cur')}
                            aria-current={p === cur ? 'page' : undefined}
                            aria-label={`Page ${p + 1}`}
                            onClick={() => setPage(p)}
                        >
                            {p + 1}
                        </button>
                    )))}
                    <Button variant="icon" aria-label="Next page" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)}>
                        <Icon name="chevronRight" size={20} />
                    </Button>
                </nav>
            </>
        );
    }

    return (
        <div className="eg-page sl-page">
            <div className="sl-head">
                <h1 className="eg-h1 sl-head__title">Sessions</h1>
                {isProvider ? (
                    <SelectField
                        label="Player"
                        value={who}
                        onChange={(v) => {
                            setWho(v);
                            setPage(0);
                            // one player's list has no Player column: its sort leaves the address with it
                            if (sort.key === 'who' && v !== 'all' && v !== user?.user_id) setSort(DEFAULT_SORT);
                        }}
                        options={playerOpts}
                        className="sl-ctl"
                    />
                ) : null}
                <div className="sl-ctl">
                    <SearchField value={q} onChange={(v) => { setQ(v); setPage(0); }} placeholder={isProvider ? 'Search game, player or guest' : 'Search game'} label="Search sessions" />
                </div>
                {isMobile ? (
                    // cards have no headers to click
                    <SelectField
                        label="Sort"
                        value={`${sort.key}:${sort.dir}`}
                        onChange={(v) => { const [key, dir] = v.split(':'); setSort({ key, dir }); }}
                        options={sortOptions(showWho)}
                        className="sl-ctl"
                    />
                ) : null}
            </div>
            {body}
        </div>
    );
}
